import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Grid, Chip, CircularProgress, Divider, useMediaQuery, useTheme, FormControl, InputLabel, Select, MenuItem, OutlinedInput, InputAdornment,  } from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Straighten as MeasureIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import client from '../../api/client';
import CustomTextField from '../../components/CustomTextField';
import { toPersianDigits } from '../../utils/numberConverter';

const themeColors = {
  dark: '#1a1a2e',
  gold: '#d4af37',
  goldLight: '#f4e4bc',
};

const emptyMeasurement = {
  location: '',
  height: '',
  width: '',
  fabric_types: [],
  description: '',
};

const AddMeasurement = () => {
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [fabricItems, setFabricItems] = useState([]);
  const [measurements, setMeasurements] = useState([{ ...emptyMeasurement }]);
  const [order, setOrder] = useState(null);

  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchFabricItems();
    fetchOrder();
  }, []);

  const fetchFabricItems = async () => {
    try {
      const response = await client.get('/orders/items/', { params: { type: 'fabric' } });
      setFabricItems(response.data);
    } catch (error) {
      console.error('Error fetching fabric items:', error);
    }
  };

  const fetchOrder = async () => {
    try {
      const response = await client.get(`/orders/${id}/`);
      setOrder(response.data);
    } catch (error) {
      toast.error('خطا در دریافت اطلاعات سفارش');
      handleClose();
    }
  };

  const handleClose = () => {
    setOpen(false);
    navigate(`/shop/orders/${id}`);
  };

  const handleAddMeasurement = () => {
    setMeasurements([...measurements, { ...emptyMeasurement }]);
  };

  const handleRemoveMeasurement = (index) => {
    if (measurements.length === 1) {
      toast.warning('حداقل یک اندازه‌گیری لازم است');
      return;
    }
    const newMeasurements = measurements.filter((_, i) => i !== index);
    setMeasurements(newMeasurements);
  };

  const handleMeasurementChange = (index, field, value) => {
    const newMeasurements = [...measurements];
    newMeasurements[index][field] = value;
    setMeasurements(newMeasurements);
  };

  const handleSubmit = async () => {
    // اعتبارسنجی
    for (let i = 0; i < measurements.length; i++) {
      const m = measurements[i];
      if (!m.location.trim()) {
        toast.error(`محل در اندازه‌گیری ${i + 1} الزامی است`);
        return;
      }
      if (!m.height || parseFloat(m.height) <= 0) {
        toast.error(`ارتفاع در اندازه‌گیری ${i + 1} الزامی است`);
        return;
      }
      if (!m.width || parseFloat(m.width) <= 0) {
        toast.error(`عرض در اندازه‌گیری ${i + 1} الزامی است`);
        return;
      }
    }

    setLoading(true);
    try {
      await client.post(`/orders/${id}/measurements/`, {
        measurements: measurements.map(m => ({
          ...m,
          height: parseFloat(m.height),
          width: parseFloat(m.width),
        }))
      });
      toast.success('اندازه‌گیری‌ها با موفقیت ثبت شد');
      navigate(`/shop/orders/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'خطا در ثبت اندازه‌گیری');
    } finally {
      setLoading(false);
    }
  };

  

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          minHeight: isMobile ? '100%' : 'auto',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: themeColors.dark,
          color: themeColors.gold,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MeasureIcon />
          <Typography variant="h6" fontWeight="bold">
            ثبت اندازه‌گیری
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: themeColors.gold }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {/* اطلاعات سفارش */}
        {order && (
          <Card sx={{ mb: 2, bgcolor: 'grey.100' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="body2" color="text.secondary">
                سفارش: <strong>{order.order_number}</strong> | 
                مشتری: <strong>{order.customer_name}</strong>
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* لیست اندازه‌گیری‌ها */}
        {measurements.map((measurement, index) => (
          <Card key={index} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  اندازه‌گیری {toPersianDigits((index + 1).toString())}
                </Typography>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoveMeasurement(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>

              <Grid container spacing={2}>
                {/* محل */}
                <Grid item xs={12}>
                  <CustomTextField                     fullWidth
                    label="محل *"
                    placeholder="مثال: پذیرایی، اتاق ۱، آشپزخانه..."
                    value={measurement.location}
                    onChange={(e) => handleMeasurementChange(index, 'location', e.target.value)}
                  />
                </Grid>

                {/* ارتفاع */}
                <Grid item xs={6}>
                  <Box dir="ltr">
                    <CustomTextField
                      fullWidth
                      label="ارتفاع *"
                      type="number"
                      value={measurement.height}
                      onChange={(e) => handleMeasurementChange(index, 'height', e.target.value)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                      }}
                    />
                  </Box>
                </Grid>

                {/* عرض */}
                <Grid item xs={6}>
                  <Box dir="ltr">
                    <CustomTextField
                      fullWidth
                      label="عرض *"
                      type="number"
                      value={measurement.width}
                      onChange={(e) => handleMeasurementChange(index, 'width', e.target.value)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                      }}
                    />
                  </Box>
                </Grid>

                {/* نوع پارچه */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>نوع پارچه</InputLabel>
                    <Select
                      multiple
                      value={measurement.fabric_types}
                      onChange={(e) => handleMeasurementChange(index, 'fabric_types', e.target.value)}
                      input={<OutlinedInput label="نوع پارچه" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((id) => {
                            const item = fabricItems.find(f => f.id === id);
                            return item ? (
                              <Chip key={id} label={item.name} size="small" />
                            ) : null;
                          })}
                        </Box>
                      )}
                    >
                      {fabricItems.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* توضیحات */}
                <Grid item xs={12}>
                  <CustomTextField                     fullWidth
                    label="توضیحات"
                    multiline
                    rows={2}
                    value={measurement.description}
                    onChange={(e) => handleMeasurementChange(index, 'description', e.target.value)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}

        {/* دکمه اضافه کردن */}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddMeasurement}
          sx={{
            py: 1.5,
            borderStyle: 'dashed',
            borderWidth: 2,
          }}
        >
          افزودن اندازه‌گیری جدید
        </Button>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={handleClose}>
          انصراف
        </Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            flexGrow: 1,
            bgcolor: themeColors.gold,
            color: themeColors.dark,
            '&:hover': { bgcolor: themeColors.goldLight },
          }}
        >
          ثبت اندازه‌گیری‌ها
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMeasurement;